import type { VirtualElement } from '@tunasong/ui-lib'
import { useSyncExternalStore } from 'react'

/**
 * The exchange whose card is open after reopening it from its marker (Option A §3, "Reopen"). A live inline turn
 * has its own store (`inline-agent-turn.ts`); this is for a persisted exchange. Opening one closes any other: one
 * card at a time.
 */
export interface OpenExchange {
  agentId: string
  exchangeId: string
  /** The document it's about; only that editor's host renders it (two editors on one screen) */
  hostId: string
  anchorEl: HTMLElement | VirtualElement | null
  /** The marker that opened it: focus returns there on collapse, and its own click toggles the card (Joni, #3067) */
  opener?: HTMLElement | null
}

type Listener = () => void
const listeners = new Set<Listener>()
let open: OpenExchange | null = null

const notify = () => {
  for (const listener of listeners) {
    listener()
  }
}

export const openExchangeCard = (exchange: OpenExchange): void => {
  open = exchange
  notify()
}

/** Collapse: the card shrinks back to its marker; nothing is discarded */
export const collapseExchangeCard = (): void => {
  open = null
  notify()
}

export const getOpenExchange = (): OpenExchange | null => open

const subscribe = (listener: Listener) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export const useOpenExchange = (): OpenExchange | null =>
  useSyncExternalStore(subscribe, getOpenExchange, getOpenExchange)
